import { Check, Layers, Loader2, Save, Send } from 'lucide-react'
import { BASKET_MAX_LEGS } from '@apna/shared'
import { cn } from '@/lib/utils'
import { Notice } from '@/components/Notice'
import type { BasketLegDto } from '@/types/basket'
import { PreTradeChecklist } from '@/screens/terminal/PreTradeChecklist'
import { BasketLegRow } from './BasketLegRow'
import { BasketSummary } from './BasketSummary'
import type { BasketPreview } from '../hooks/useBasketPreview'
import { basketSides, expiryCount } from '../utils/basketPreview'

/**
 * The basket itself — ROADMAP 9.66.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE INTENT, HOWEVER MANY LEGS, AND NO CONFIRMATION AFTER IT
 *
 * "Place basket" sends. There is no second dialog, for the same reason the
 * ladder's B and S have none (4.15) and the strategy ticket's typed word was
 * removed: the trader assembled these ten rows by hand and has been reading
 * the margin and the payoff the whole time. The basket IS the confirmation.
 * The checklist below is a PAUSE, not a gate — it can be skipped, and the
 * skip is recorded.
 *
 * What that costs, stated once: on a LIVE account this is a real ten-leg
 * position, immediately, with no undo. The protections that remain are the
 * ones the order path enforces and a click cannot skip — the account must be
 * chosen, the market must be open, every leg is gated and priced
 * individually, and the whole basket carries one idempotency key, so a double
 * click cannot become twenty legs.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SAVE AND PLACE ARE TWO DIFFERENT INTENTIONS — ROADMAP 9.67
 *
 * Place writes the row and then sends it. Save writes the row and stops,
 * which is what a trader building a structure for tomorrow morning actually
 * wants — and what the basket drawer was for. They are separate buttons
 * because one of them puts money at risk and the other does not, and a
 * single button that sometimes did each would be the worst of both.
 *
 * Closing the builder saves too. A basket assembled and then abandoned at
 * the cross used to vanish, which is the one thing a draft exists to
 * prevent.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE CHECKLIST IS ASKED HERE, AND THAT IS THE WHOLE POINT — ROADMAP 9.69
 *
 * A basket opened from a strategy's ticket by Adjustments carries that
 * strategy's checklist. It is asked HERE rather than back in the ticket
 * because the rules are claims about specific strikes — "resistance above
 * the short strike is identified" — and a tick made before the trader moved
 * that strike describes a structure that is not being sent.
 *
 * AFTER the legs and the figures, for the reason the ticket gives: the
 * checks are about whether to take a trade that has already been described,
 * and asking them above the numbers asks them about nothing.
 *
 * A basket built from scratch has no strategy and no checklist, so nothing
 * is drawn and nothing is gated. That is not a loophole — it is what a
 * hand-built basket has always been.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE NAME IS THE TRADE'S LABEL
 *
 * Not a note on a draft. `Trade.label` is what makes the Positions drawer
 * draw a card instead of a row — "a group is a trade with a name", and this
 * box is where that name is written. It reaches the Trade off the saved row
 * rather than off the request; see `basketPlace.service.ts`.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export interface BasketPanelProps {
  name: string
  onName: (name: string) => void
  legs: readonly BasketLegDto[]
  onLots: (token: number, lots: number) => void
  onRemove: (token: number) => void
  preview: BasketPreview
  spot: number
  /** Null when no account is selected; the button then says so. */
  /** The seeding strategy's id and checklist, when there is one — 9.69. */
  checklist?: { id: string; checklist: readonly string[] } | undefined
  /** What the checklist says: may-place, was-completed, and the ticks. */
  onChecklist: (
    ready: boolean,
    done: boolean,
    result: readonly { rule: string; ticked: boolean }[],
  ) => void
  /** False while a checklist is neither ticked nor skipped. */
  checklistReady: boolean
  accountLabel: string | null
  placing: boolean
  saving: boolean
  /** True once the last save landed and nothing has changed since. */
  saved: boolean
  error: string | null
  onPlace: () => void
  onSave: () => void
}

export function BasketPanel({
  name,
  onName,
  legs,
  onLots,
  onRemove,
  preview,
  spot,
  checklist,
  onChecklist,
  checklistReady,
  accountLabel,
  placing,
  saving,
  saved,
  error,
  onPlace,
  onSave,
}: BasketPanelProps) {
  const { buyLots, sellLots } = basketSides(legs)
  /*
   * The expiry is drawn on a leg only when the basket holds more than one.
   * On the single-expiry basket that is almost all of them it is the same
   * date ten times, which is ten copies of something the heading could say.
   */
  const showExpiry = expiryCount(legs) > 1
  const full = legs.length >= BASKET_MAX_LEGS
  const ready =
    legs.length > 0 && accountLabel !== null && !placing && !saving && checklistReady

  return (
    <aside className="bb-panel" aria-label="Basket">
      <header className="bb-panel-head">
        <Layers size={14} className="shrink-0 text-muted-foreground" />
        <input
          value={name}
          onChange={(e) => onName(e.target.value)}
          aria-label="Basket name"
          data-testid="basket-name"
          placeholder="Name this basket"
          maxLength={48}
          className="bb-name"
        />
        <span
          className={cn('bb-count num', full && 'full')}
          title={`A basket holds ${BASKET_MAX_LEGS} legs`}
        >
          {legs.length}/{BASKET_MAX_LEGS}
        </span>
      </header>

      {/*
        ── ONE SCROLL, NOT FOUR STACKED REGIONS — ROADMAP 9.69 ────────────
        The legs, the figures, the payoff and the checklist scroll TOGETHER.
        Each of them owning a scrollbar meant the checklist sat in a 168px
        box with its third row sliced through the middle and its Skip button
        below a fold nothing indicated was there — on the one control whose
        whole purpose is to be read before an order goes out.

        Only the name and the two buttons are pinned, because those are the
        two things that must be reachable from anywhere in the list.
      */}
      <div className="bb-body">
      {legs.length > 0 && (
        <p className="bb-sides num">
          {buyLots > 0 && <span className="buy">+{buyLots}</span>}
          {sellLots > 0 && <span className="sell">−{sellLots}</span>}
          <span className="text-muted-foreground">lots</span>
        </p>
      )}

      <div className="bb-legs" data-testid="basket-legs">
        {legs.length === 0 ? (
          <p className="bb-empty">
            Click <b>+B</b> or <b>+S</b> on the ladder to add a leg. Clicking the
            other side of a contract you already hold takes lots off it rather
            than adding a second row.
          </p>
        ) : (
          legs.map((leg) => (
            <BasketLegRow
              key={leg.token}
              leg={leg}
              showExpiry={showExpiry}
              onLots={onLots}
              onRemove={onRemove}
            />
          ))
        )}
      </div>

      <BasketSummary preview={preview} spot={spot} />

      {checklist && (
        <div className="bb-check">
          <PreTradeChecklist
            strategy={{ id: checklist.id, checklist: [...checklist.checklist] }}
            /*
             * Keyed on the BASKET, so the ticks reset when a different one is
             * opened and survive every edit to this one. Keying it on the
             * legs would clear the trader's ticks the moment they adjusted a
             * lot — which is the opposite of what the checks are for.
             */
            resetKey={checklist.id}
            onReadyChange={onChecklist}
            /*
             * Skip SENDS, as it does in the ticket: a trader who has decided
             * to go without the checks has decided, and a second click on
             * another button teaches them to click twice without reading.
             */
            onSkip={onPlace}
          />
        </div>
      )}

      {error && (
        <Notice tone="error" className="mx-3 mb-2" testId="basket-error">
          {error}
        </Notice>
      )}
      </div>

      <footer className="bb-panel-foot">
        <div className="bb-acts">
          <button
            type="button"
            className="bb-save"
            data-testid="save-basket"
            disabled={legs.length === 0 || saving || placing}
            onClick={onSave}
            title="Keep this basket without sending it"
          >
            {saving ? (
              <Loader2 size={13} className="animate-spin" />
            ) : saved ? (
              <Check size={13} />
            ) : (
              <Save size={13} />
            )}
            {saving ? 'Saving…' : saved ? 'Saved' : 'Save'}
          </button>
          <button
            type="button"
            className="bb-place"
            data-testid="place-basket"
            disabled={!ready}
            onClick={onPlace}
          >
          {placing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {placing
              ? 'Sending…'
              : legs.length === 0
                ? 'Add a leg'
                : accountLabel === null
                  ? 'Choose an account'
                  : !checklistReady
                    ? 'Confirm the checks below'
                    : `Place ${legs.length} leg${legs.length === 1 ? '' : 's'}`}
          </button>
        </div>
        {accountLabel !== null && legs.length > 0 && (
          <span className="bb-acct">on {accountLabel}</span>
        )}
      </footer>
    </aside>
  )
}
